<template>
    <b-modal
        :id="modalId"
        title="벤더코드 벌크 등록"
        size="xl"
        scrollable
        no-close-on-backdrop
        hide-footer
        @hidden="onHidden"
    >
        <b-tabs v-model="activeTab" content-class="mt-3">
            <!-- 탭1: 엑셀 파일로 등록 (다중 프로젝트) -->
            <b-tab title="엑셀 파일로 등록">
                <p class="text-secondary">
                    엑셀 양식을 다운로드해 작성한 뒤 업로드하세요.
                    양식은 행마다 project_code를 지정할 수 있어 여러 프로젝트를 한 번에 등록할 수 있습니다.
                </p>
                <div class="mb-3">
                    <b-button variant="outline-secondary" size="sm" class="mr-2" @click="downloadTemplate">
                        <fa icon="download" class="mr-1"/>양식 다운로드 (.xlsx)
                    </b-button>
                </div>
                <b-form-group label="엑셀 파일 업로드" label-for="file-bulk-upload-input">
                    <b-form-file
                        id="file-bulk-upload-input"
                        v-model="fileUploadModel"
                        accept=".xlsx,.xls"
                        placeholder="파일을 선택하거나 드래그하세요"
                        browse-text="파일 선택"
                    />
                </b-form-group>
                <b-alert v-if="fileBulkResult" show variant="success">
                    <strong>{{ fileBulkResult.total_count }}건</strong> 등록 완료
                </b-alert>
            </b-tab>

            <!-- 탭2: 표에서 직접 등록 (단일 프로젝트) -->
            <b-tab title="표에서 직접 등록">
                <!-- Step 1: 프로젝트 코드 -->
                <b-form-group label="프로젝트 코드" label-class="font-weight-bold">
                    <b-form-input
                        v-model="projectCode"
                        placeholder="예: PRJ_2026_A"
                        :state="projectCodeState"
                        @input="onProjectCodeChange"
                    />
                    <b-form-invalid-feedback>{{ projectCodeError }}</b-form-invalid-feedback>
                    <b-form-text>이 탭은 단일 프로젝트 코드 기준으로만 등록합니다.</b-form-text>
                </b-form-group>

                <!-- 도구바 -->
                <div class="d-flex flex-wrap align-items-center mb-2 toolbar">
                    <b-form-file
                        v-model="uploadFile"
                        accept=".xlsx,.xls,.csv"
                        placeholder="파일 업로드 (.xlsx / .csv)"
                        browse-text="찾기"
                        size="sm"
                        class="mr-2 mb-1 file-input"
                        @input="onFileSelected"
                    />
                    <small class="text-secondary mb-1">또는 표에 직접 입력 / 표 위에 <kbd>Ctrl</kbd>+<kbd>V</kbd> 로 붙여넣기</small>
                    <b-form-checkbox
                        v-if="hasIssueRows"
                        v-model="showOnlyIssues"
                        class="ml-3 mb-1 issue-filter"
                    >
                        이슈 행만 표시 <span class="text-muted">({{ issueRowCount }}건)</span>
                    </b-form-checkbox>
                    <span class="mr-auto"></span>
                    <b-button size="sm" variant="outline-secondary" class="mr-2 mb-1" @click="addRow">
                        <fa icon="plus" class="mr-1"/>행 추가
                    </b-button>
                    <b-button size="sm" variant="outline-danger" class="mb-1" :disabled="rows.length === 0" @click="clearRows">
                        모두 지우기
                    </b-button>
                </div>

                <!-- 표 -->
                <div
                    class="bulk-table-wrapper"
                    tabindex="0"
                    @paste="handlePaste"
                >
                    <table class="table table-sm table-bordered mb-0 bulk-table">
                        <thead class="thead-light">
                            <tr>
                                <th class="text-center idx-col">#</th>
                                <th class="company-id-col">회사 ID</th>
                                <th>회사명</th>
                                <th class="vendor-code-col">벤더코드 <small class="text-muted">(비우면 자동)</small></th>
                                <th class="status-col">상태</th>
                                <th class="action-col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in displayRows" :key="row._uid" :class="rowClass(row)">
                                <td class="text-center text-muted">{{ rowNumber(row) }}</td>
                                <td>
                                    <b-form-input
                                        v-model="row.company_id"
                                        size="sm"
                                        type="number"
                                        placeholder="선택"
                                        @input="onCellEdit(row)"
                                    />
                                </td>
                                <td>
                                    <b-form-input
                                        v-model="row.company_name"
                                        size="sm"
                                        placeholder="company_id 없으면 필수"
                                        @input="onCellEdit(row)"
                                    />
                                </td>
                                <td>
                                    <b-input-group size="sm">
                                        <b-form-input
                                            v-model="row.vendor_code"
                                            placeholder="자동 생성"
                                            @input="onCellEdit(row)"
                                        />
                                        <b-input-group-append v-if="row._autoFilledVendorCode">
                                            <b-button
                                                v-b-tooltip.hover
                                                variant="outline-secondary"
                                                :title="'재발급 (칸을 비우고 다시 검증을 누르면 새 코드를 받습니다)'"
                                                class="regen-btn"
                                                @click="regenerateVendorCode(row)"
                                            ><fa icon="redo"/></b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                </td>
                                <td>
                                    <template v-if="row.status === 'ok'">
                                        <b-badge variant="success">OK</b-badge>
                                        <div v-if="row.result" class="status-detail">
                                            <div>{{ actionLabel(row.result.action) }}</div>
                                        </div>
                                    </template>
                                    <template v-else-if="row.status === 'error'">
                                        <b-badge variant="danger">실패</b-badge>
                                        <div class="status-detail text-danger">
                                            {{ row.error && row.error.message }}
                                        </div>
                                        <div
                                            v-if="row.error && row.error.candidate_company_id != null"
                                            class="status-detail mt-1"
                                        >
                                            <div v-if="row.error.candidate_company_name" class="text-muted candidate-name">
                                                후보: {{ row.error.candidate_company_name }}<span v-if="row.error.candidate_company_code"> · {{ row.error.candidate_company_code }}</span>
                                            </div>
                                            <b-button
                                                v-b-tooltip.hover
                                                size="sm"
                                                variant="outline-secondary"
                                                :title="`회사 ID ${row.error.candidate_company_id} 클립보드 복사`"
                                                class="copy-id-btn"
                                                @click="copyCandidateCompanyId(row.error.candidate_company_id)"
                                            >
                                                <fa icon="copy" class="mr-1"/>회사 ID 복사 ({{ row.error.candidate_company_id }})
                                            </b-button>
                                        </div>
                                    </template>
                                    <span v-else class="text-muted small">대기</span>
                                </td>
                                <td class="text-center">
                                    <b-button size="sm" variant="link" class="text-danger p-0 remove-btn" @click="removeRow(row)">
                                        <fa icon="times"/>
                                    </b-button>
                                </td>
                            </tr>
                            <tr v-if="rows.length === 0">
                                <td colspan="6" class="text-center text-muted py-4">
                                    행이 없습니다. <strong>"행 추가"</strong> · <strong>파일 업로드</strong> · <strong>붙여넣기</strong> 중 하나로 시작하세요.
                                </td>
                            </tr>
                            <tr v-else-if="displayRows.length === 0">
                                <td colspan="6" class="text-center text-muted py-4">
                                    이슈가 있는 행이 없습니다. <strong>"이슈 행만 표시"</strong>를 해제하면 전체 행이 보입니다.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- 결과 요약 -->
                <b-alert v-if="dryRunResult" :variant="summaryVariant" show class="mt-3 mb-0">
                    <fa :icon="dryRunResult.invalid_count === 0 ? 'check-circle' : 'exclamation-triangle'" class="mr-1"/>
                    검증 결과: 성공 <strong>{{ dryRunResult.valid_count }}</strong>건 / 실패 <strong>{{ dryRunResult.invalid_count }}</strong>건
                    (총 {{ dryRunResult.total_count }}건)
                    <span v-if="dryRunResult.invalid_count === 0" class="ml-1">— 이제 등록을 진행할 수 있습니다.</span>
                    <span v-else class="ml-1">— 실패한 행을 수정한 뒤 다시 검증하세요.</span>

                    <!-- 이슈 종합 + 일괄 액션 -->
                    <div v-if="duplicateRows.length > 0" class="mt-2 pt-2 issue-actions">
                        <fa icon="exclamation-circle" class="mr-1"/>
                        <strong>중복(기존 업체) {{ duplicateRows.length }}건</strong>
                        — 충돌한 기존 회사 ID를 회사 ID 칸에 한 번에 채울 수 있습니다.
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            class="ml-2 bulk-apply-btn"
                            @click="bulkApplyCandidateIds"
                        >
                            <fa icon="copy" class="mr-1"/>회사 ID 일괄 적용 ({{ duplicateRows.length }}건)
                        </b-button>
                        <div class="text-muted small mt-1">
                            적용 후 <strong>다시 검증</strong>을 누르면 "기존 업체 사용"으로 처리됩니다.
                        </div>
                    </div>
                </b-alert>
            </b-tab>
        </b-tabs>

        <!-- 푸터 액션 -->
        <div class="d-flex justify-content-end mt-3 footer-actions">
            <b-button variant="secondary" class="mr-2" :disabled="busy || fileBulkBusy" @click="hide">취소</b-button>

            <!-- 탭1 활성 시 -->
            <template v-if="activeTab === 0">
                <b-button
                    variant="primary"
                    :disabled="!fileUploadModel || fileBulkBusy"
                    @click="submitFileBulkUpload"
                >
                    <b-spinner v-if="fileBulkBusy" small class="mr-1"/>
                    <fa v-else icon="upload" class="mr-1"/>
                    등록
                </b-button>
            </template>

            <!-- 탭2 활성 시 -->
            <template v-else>
                <b-button
                    variant="outline-primary"
                    class="mr-2"
                    :disabled="!canValidate || busy"
                    @click="runDryRun"
                >
                    <b-spinner v-if="busy === 'dry-run'" small class="mr-1"/>
                    <fa v-else icon="check" class="mr-1"/>
                    검증 (dry-run)
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="!canSubmit || busy"
                    @click="runSubmit"
                >
                    <b-spinner v-if="busy === 'submit'" small class="mr-1"/>
                    <fa v-else icon="upload" class="mr-1"/>
                    등록 ({{ submittableRowCount }}건)
                </b-button>
            </template>
        </div>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import * as XLSX from 'xlsx';

// 백엔드는 없는 프로젝트 코드에 404 + 평문 "Not found project_codes : <코드>" 를 준다.
// 반환: { msg, missingCode } — missingCode 가 있으면 프로젝트 코드 문제.
function pushBulkError(e, fallback) {
    const raw = getErrorMessage(e, '');
    const m = /^Not found project_codes\s*:\s*(.+)$/.exec(raw);
    if (m) {
        const code = m[1].trim();
        return { msg: `프로젝트 코드 '${code}' 를 찾을 수 없습니다. 코드를 확인해 주세요.`, missingCode: code };
    }
    return { msg: raw || fallback, missingCode: null };
}

let _uidSeq = 0;
function makeRow(seed = {}) {
    return {
        _uid: ++_uidSeq,
        // 엑셀 파싱 시 셀 값이 number 로 들어올 수 있어(숫자만 있는 코드 등)
        // 모든 입력값을 문자열로 정규화한다. (number.trim is not a function 방지)
        company_id: seed.company_id != null ? String(seed.company_id) : '',
        company_name: seed.company_name != null ? String(seed.company_name) : '',
        vendor_code: seed.vendor_code != null ? String(seed.vendor_code) : '',
        status: 'pending',
        result: null,
        error: null,
        _autoFilledVendorCode: false,
    };
}

const ACTION_LABELS = {
    use_existing_company: '기존 업체 사용',
    update_company_name: '기존 업체 이름 갱신',
    create_new_company: '새 업체 생성',
};

export default {
    name: 'VendorCodeBulkUploadModal',
    props: {
        modalId: { type: String, default: 'bulk-upload-modal' },
    },
    data() {
        return {
            // 탭 상태
            activeTab: 0,
            // 탭1 상태
            fileUploadModel: null,
            fileBulkBusy: false,
            fileBulkResult: null,
            // 탭2 상태
            projectCode: '',
            // dry-run/등록이 "없는 프로젝트 코드" 로 실패했을 때의 안내 문구 (입력 수정 시 해제)
            projectCodeError: null,
            rows: [],
            uploadFile: null,
            dryRunResult: null,
            busy: null,
            // 검증 후 이슈(실패) 행만 표시하는 필터
            showOnlyIssues: false,
        };
    },
    computed: {
        projectCodeState() {
            if (this.projectCode === '') return null;
            if (this.projectCodeError) return false;
            return this.projectCode.trim().length > 0;
        },
        nonEmptyRows() {
            return this.rows.filter(r => r.company_id || r.company_name || r.vendor_code);
        },
        submittableRowCount() {
            return this.nonEmptyRows.length;
        },
        canValidate() {
            return !!this.projectCode.trim() && this.nonEmptyRows.length > 0;
        },
        canSubmit() {
            if (!this.canValidate) return false;
            if (!this.dryRunResult) return false;
            return this.dryRunResult.invalid_count === 0
                && this.dryRunResult.total_count === this.nonEmptyRows.length;
        },
        summaryVariant() {
            if (!this.dryRunResult) return 'secondary';
            return this.dryRunResult.invalid_count === 0 ? 'success' : 'warning';
        },
        // 검증 실패(error) 행이 하나라도 있는지 — "이슈 행만 표시" 토글 노출 조건
        hasIssueRows() {
            return this.rows.some(r => r.status === 'error');
        },
        issueRowCount() {
            return this.rows.filter(r => r.status === 'error').length;
        },
        // 표에 실제로 렌더링할 행 (필터 on이면 error 행만, 단 행 자체는 보존)
        displayRows() {
            if (this.showOnlyIssues) {
                return this.rows.filter(r => r.status === 'error');
            }
            return this.rows;
        },
        // 중복(기존 업체 충돌) 행 — candidate_company_id 로 일괄 해결 가능한 대상
        duplicateRows() {
            return this.rows.filter(
                r => r.status === 'error' && r.error && r.error.candidate_company_id != null,
            );
        },
    },
    methods: {
        actionLabel(action) {
            return ACTION_LABELS[action] || action || '-';
        },
        rowClass(row) {
            if (row.status === 'ok') return 'row-ok';
            if (row.status === 'error') return 'row-error';
            return '';
        },

        // ── 행 조작 ────────────────────────────────────────────────────
        addRow() {
            this.rows.push(makeRow());
            this.invalidateValidation();
        },
        removeRow(row) {
            // 필터(이슈만 보기) 적용 시 표의 인덱스와 rows 인덱스가 어긋나므로
            // 행 객체(_uid) 기준으로 실제 위치를 찾아 삭제한다.
            const idx = this.rows.findIndex(r => r._uid === row._uid);
            if (idx < 0) return;
            this.rows.splice(idx, 1);
            this.invalidateValidation();
        },
        // 표의 # 번호는 필터와 무관하게 원본 입력 순서를 보여준다.
        rowNumber(row) {
            return this.rows.findIndex(r => r._uid === row._uid) + 1;
        },
        clearRows() {
            this.rows = [];
            this.invalidateValidation();
        },
        onCellEdit(row) {
            row.status = 'pending';
            row.result = null;
            row.error = null;
            row._autoFilledVendorCode = false;
            this.dryRunResult = null;
            this.showOnlyIssues = false;
        },
        invalidateValidation() {
            this.dryRunResult = null;
            // 검증이 무효화되면 표시할 이슈 행이 없어지므로 필터를 해제한다.
            this.showOnlyIssues = false;
            this.rows.forEach(r => {
                r.status = 'pending';
                r.result = null;
                r.error = null;
            });
        },
        onProjectCodeChange() {
            this.projectCodeError = null;
            this.invalidateValidation();
        },

        // ── 붙여넣기 ───────────────────────────────────────────────────
        handlePaste(evt) {
            // 표의 셀 input에서 발생한 paste는 셀 자체 동작을 우선
            const tag = (evt.target && evt.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            const text = evt.clipboardData && evt.clipboardData.getData('text/plain');
            if (!text) return;
            evt.preventDefault();
            this.appendRowsFromText(text);
        },
        appendRowsFromText(text) {
            const lines = text.replace(/\r\n?/g, '\n').split('\n').filter(l => l.length > 0);
            if (lines.length === 0) return;

            const parsed = lines.map(line => {
                // 탭 우선, 없으면 콤마
                const sep = line.includes('\t') ? '\t' : ',';
                return line.split(sep).map(c => c.trim());
            });

            // 첫 줄이 헤더로 보이면 스킵 (company_id / company_name / vendor_code 키워드 포함)
            const first = parsed[0].join(' ').toLowerCase();
            const hasHeader = ['company_id', 'company_name', 'vendor_code', '회사', '업체'].some(k => first.includes(k));
            const dataRows = hasHeader ? parsed.slice(1) : parsed;

            const before = this.rows.length;
            dataRows.forEach(cols => {
                const [c1, c2, c3] = cols;
                const row = this.coerceRow(c1, c2, c3);
                if (row) this.rows.push(row);
            });
            this.invalidateValidation();
            this.$bvToast.toast(`${this.rows.length - before}건의 행을 추가했습니다.`, {
                title: '붙여넣기', variant: 'info', autoHideDelay: 2000,
            });
        },
        coerceRow(c1, c2, c3) {
            // c1이 숫자처럼 보이면 company_id로, 아니면 company_name으로 해석
            const trimmed = [c1, c2, c3].map(v => (v == null ? '' : String(v).trim()));
            if (trimmed.every(v => v === '')) return null;
            const [a, b, c] = trimmed;
            if (/^\d+$/.test(a)) {
                return makeRow({ company_id: a, company_name: b, vendor_code: c });
            }
            // a가 숫자가 아니면: a=company_name, b=vendor_code (c는 무시)
            return makeRow({ company_id: '', company_name: a, vendor_code: b });
        },

        // ── 파일 업로드 (탭2: 표에 채우기) ───────────────────────────────
        async onFileSelected(file) {
            if (!file) return;
            const name = (file.name || '').toLowerCase();
            try {
                if (name.endsWith('.csv')) {
                    const text = await file.text();
                    this.appendRowsFromText(text);
                } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
                    await this.parseXlsxFile(file);
                } else {
                    this.$bvToast.toast('지원하지 않는 파일 형식입니다. (.xlsx / .csv)', {
                        title: '오류', variant: 'danger',
                    });
                }
            } catch (e) {
                console.error(e); // eslint-disable-line no-console
                this.$bvToast.toast('파일 파싱 중 오류가 발생했습니다.', {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.uploadFile = null;
            }
        },
        // 시트 선택 + 헤더 행 탐색 (탭1 요약, 탭2 채우기 공용)
        async readSheetRows(file) {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: 'array' });
            // 'vendor codes' 시트가 있으면 우선, 없으면 첫 시트
            const sheetName = wb.SheetNames.find(s => s.toLowerCase().includes('vendor')) || wb.SheetNames[0];
            const ws = wb.Sheets[sheetName];
            const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
            // 백엔드 양식은 헤더가 3번째 행에 있고 그 위에 안내 문구가 있음.
            // 'company_id' 셀이 보이는 행을 헤더로 간주한다.
            const headerIdx = aoa.findIndex(row =>
                row.some(c => String(c).trim().toLowerCase() === 'company_id'),
            );
            return { aoa, headerIdx };
        },
        async parseXlsxFile(file) {
            const { aoa, headerIdx } = await this.readSheetRows(file);
            if (headerIdx < 0) {
                // 헤더가 없으면 모든 행을 데이터로 간주
                aoa.forEach(r => {
                    const row = this.coerceRow(r[0], r[1], r[2]);
                    if (row) this.rows.push(row);
                });
                this.invalidateValidation();
                return;
            }
            const header = aoa[headerIdx].map(c => String(c).trim().toLowerCase());
            const col = (name) => header.indexOf(name);
            const idxCompanyId = col('company_id');
            const idxCompanyName = col('company_name');
            const idxProjectCode = col('project_code');
            const idxVendorCode = col('vendor_code');
            const dataRows = aoa.slice(headerIdx + 1).filter(r => r && !r.every(c => String(c).trim() === ''));

            // 이 탭은 단일 프로젝트 기준이다. 서로 다른 project_code 가 섞인 파일은 첫 코드로 몰려 등록되므로 막는다.
            const codes = idxProjectCode < 0 ? [] : [...new Set(
                dataRows.map(r => String(r[idxProjectCode] ?? '').trim()).filter(Boolean),
            )];
            if (codes.length > 1) {
                this.$bvToast.toast(
                    `파일에 서로 다른 project_code ${codes.length}개(${codes.join(', ')})가 섞여 있습니다. 이 탭은 단일 프로젝트용이므로 [엑셀 파일로 등록] 탭을 사용하세요.`,
                    { title: '다중 프로젝트 파일', variant: 'danger', autoHideDelay: 8000 },
                );
                return;
            }
            const projectCodeFromFile = codes[0] || null;
            if (projectCodeFromFile && this.projectCode.trim() && this.projectCode.trim() !== projectCodeFromFile) {
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: '프로젝트 코드 불일치',
                    message: `파일의 project_code(${projectCodeFromFile})가 입력한 프로젝트 코드(${this.projectCode.trim()})와 다릅니다. 입력한 코드(${this.projectCode.trim()})로 ${dataRows.length}건을 등록 대상으로 추가할까요?`,
                });
                if (!ok) return;
            }
            dataRows.forEach(r => {
                this.rows.push(makeRow({
                    company_id: idxCompanyId >= 0 ? r[idxCompanyId] : '',
                    company_name: idxCompanyName >= 0 ? r[idxCompanyName] : '',
                    vendor_code: idxVendorCode >= 0 ? r[idxVendorCode] : '',
                }));
            });
            const added = dataRows.length;
            // 파일에 project_code가 있고 사용자가 입력 안 했으면 자동 채움
            if (projectCodeFromFile && !this.projectCode.trim()) {
                this.projectCode = projectCodeFromFile;
            }
            this.invalidateValidation();
            this.$bvToast.toast(`${added}건의 행을 추가했습니다.`, {
                title: '파일 업로드', variant: 'info', autoHideDelay: 2000,
            });
        },
        // ── 양식 다운로드 ─────────────────────────────────────────────
        // 백엔드에서 발급한 xlsx 양식만 정식 양식으로 인정되므로
        // (push_vendor_code_bulk_with_file 의 keyword 검증 정책),
        // 양식 다운로드는 백엔드 엔드포인트를 그대로 호출한다.
        downloadTemplate() {
            window.open(`${process.env.VUE_APP_BACKENDHOST}/vendor_codes/push-bulk/template`, '_blank');
        },

        // ── 탭1: 파일 벌크 업로드 (다중 프로젝트) ──────────────────────
        async submitFileBulkUpload() {
            if (!this.fileUploadModel) return;
            this.fileBulkBusy = true;
            try {
                // 서버에 dry-run 이 없어 파일을 직접 읽어 건수/프로젝트를 확인받는다 (운영 DB 에 바로 등록되므로)
                const { aoa, headerIdx } = await this.readSheetRows(this.fileUploadModel);
                if (headerIdx < 0) {
                    this.$bvToast.toast('company_id 헤더를 찾지 못했습니다. 양식을 다시 확인해 주세요.', { title: '오류', variant: 'danger' });
                    return;
                }
                const header = aoa[headerIdx].map(c => String(c).trim().toLowerCase());
                const idxProjectCode = header.indexOf('project_code');
                const perProject = {};
                let total = 0;
                aoa.slice(headerIdx + 1).forEach(r => {
                    if (!r || r.every(c => String(c).trim() === '')) return;
                    const code = (idxProjectCode >= 0 && String(r[idxProjectCode] ?? '').trim()) || '(project_code 없음)';
                    perProject[code] = (perProject[code] || 0) + 1;
                    total += 1;
                });
                const summary = Object.entries(perProject).map(([code, n]) => `${code} ${n}건`).join(', ');
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: '벤더코드 일괄 등록',
                    message: `프로젝트 ${Object.keys(perProject).length}개, 총 ${total}건을 등록합니다. (${summary}) 운영 데이터에 바로 반영되며 되돌릴 수 없습니다.`,
                });
                if (!ok) return;

                const formData = new FormData();
                formData.append('source', this.fileUploadModel);
                const { data } = await this.$axios.post('/vendor_codes/push-bulk', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                this.fileBulkResult = data;
                // 같은 파일 재등록(중복) 방지. 결과 알림을 볼 수 있도록 모달은 닫지 않는다
                this.fileUploadModel = null;
                this.$bvToast.toast(`${data.total_count}건 등록 완료`, { title: '성공', variant: 'success' });
                this.$emit('submitted', data);
            } catch (e) {
                this.$bvToast.toast(pushBulkError(e, '등록 중 오류가 발생했습니다.').msg, { title: '오류', variant: 'danger' });
            } finally {
                this.fileBulkBusy = false;
            }
        },
        // ── 탭2: API (단일 프로젝트) ─────────────────────────────────
        buildPayload(dryRun) {
            return {
                project_code: this.projectCode.trim(),
                dry_run: dryRun,
                vendor_codes: this.nonEmptyRows.map(r => {
                    const item = {};
                    // r.* 가 number 로 들어올 수 있어(숫자만 있는 코드, dry-run 자동채움 등)
                    // String() 으로 강제 변환 후 trim 한다.
                    const companyName = r.company_name != null ? String(r.company_name).trim() : '';
                    const vendorCode = r.vendor_code != null ? String(r.vendor_code).trim() : '';
                    if (r.company_id) item.company_id = Number(r.company_id);
                    if (companyName) item.company_name = companyName;
                    if (vendorCode) item.vendor_code = vendorCode;
                    return item;
                }),
            };
        },
        applyDryRunResponse(data) {
            this.dryRunResult = {
                total_count: data.total_count,
                valid_count: data.valid_count,
                invalid_count: data.invalid_count,
            };
            const rowsByIdx = this.nonEmptyRows;
            (data.items || []).forEach(item => {
                const target = rowsByIdx[item.row_index];
                if (!target) return;
                target.status = item.status;
                target.result = item.result || null;
                target.error = item.error || null;
                // 사용자가 비워둔 칸에 provisional 코드를 자동으로 채워 WYSIWYG 일치
                if (
                    item.status === 'ok'
                    && item.result
                    && item.result.vendor_code_is_provisional
                    && item.result.vendor_code
                    && (!target.vendor_code || !String(target.vendor_code).trim())
                ) {
                    target.vendor_code = item.result.vendor_code;
                    target._autoFilledVendorCode = true;
                }
            });
            // 재검증 결과 이슈가 모두 해소되면 "이슈 행만 표시" 필터를 자동 해제한다.
            // (이슈가 없으면 필터 체크박스 자체가 사라져 사용자가 끌 수 없게 되는 것을 방지)
            if (this.showOnlyIssues && !this.rows.some(r => r.status === 'error')) {
                this.showOnlyIssues = false;
            }
        },
        async runDryRun() {
            if (!this.canValidate) return;
            this.busy = 'dry-run';
            try {
                const { data } = await this.$axios.put('/vendor_codes/push-bulk', this.buildPayload(true));
                this.applyDryRunResponse(data);
            } catch (e) {
                console.error(e); // eslint-disable-line no-console
                const { msg, missingCode } = pushBulkError(e, '검증 중 오류가 발생했습니다.');
                if (missingCode) this.projectCodeError = msg;
                this.$bvToast.toast(msg, { title: '검증 실패', variant: 'danger' });
            } finally {
                this.busy = null;
            }
        },
        async runSubmit() {
            if (!this.canSubmit) return;
            this.busy = 'submit';
            try {
                const { data } = await this.$axios.put('/vendor_codes/push-bulk', this.buildPayload(false));
                this.$bvToast.toast(`${data.total_count || data.items?.length || 0}건 등록 완료`, {
                    title: '성공', variant: 'success',
                });
                this.$emit('submitted', data);
                this.$bvModal.hide(this.modalId);
            } catch (e) {
                console.error(e); // eslint-disable-line no-console
                const { msg, missingCode } = pushBulkError(e, '등록 중 오류가 발생했습니다.');
                if (missingCode) this.projectCodeError = msg;
                this.$bvToast.toast(msg, { title: '등록 실패', variant: 'danger' });
            } finally {
                this.busy = null;
            }
        },

        // ── 재발급 ────────────────────────────────────────────────────
        regenerateVendorCode(row) {
            row.vendor_code = '';
            row._autoFilledVendorCode = false;
            this.onCellEdit(row);
        },

        // ── 중복 행 회사 ID 일괄 적용 ──────────────────────────────────
        // 중복(error + candidate_company_id) 행마다 후보 회사 ID를 회사 ID 칸에
        // 채워 넣는다. 재검증은 사용자가 직접 누른다. (적용 후 검증 무효화)
        bulkApplyCandidateIds() {
            const targets = this.duplicateRows;
            if (targets.length === 0) return;
            let applied = 0;
            targets.forEach(row => {
                const id = row.error.candidate_company_id;
                if (id == null) return;
                row.company_id = String(id);
                // 적용한 행은 다시 검증 대상이므로 상태 초기화
                row.status = 'pending';
                row.result = null;
                row.error = null;
                row._autoFilledVendorCode = false;
                applied += 1;
            });
            // 결과 요약/필터를 무효화해 "다시 검증" 을 유도
            this.dryRunResult = null;
            this.showOnlyIssues = false;
            this.$bvToast.toast(`${applied}건에 회사 ID를 적용했습니다. 다시 검증을 눌러주세요.`, {
                title: '일괄 적용 완료', variant: 'info', autoHideDelay: 2500,
            });
        },

        // ── 후보 회사 ID 복사 ─────────────────────────────────────────
        async copyCandidateCompanyId(id) {
            const text = String(id);
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const ta = document.createElement('textarea');
                    ta.value = text;
                    ta.style.position = 'fixed';
                    ta.style.opacity = '0';
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                }
                this.$bvToast.toast(`회사 ID ${text}을(를) 복사했습니다.`, {
                    title: '복사 완료', variant: 'info', autoHideDelay: 1500,
                });
            } catch (e) {
                this.$bvToast.toast('복사에 실패했습니다. 직접 선택하여 복사해주세요.', {
                    title: '오류', variant: 'danger',
                });
            }
        },

        // ── 모달 제어 ──────────────────────────────────────────────────
        hide() {
            this.$bvModal.hide(this.modalId);
        },
        onHidden() {
            // 탭2 상태
            this.projectCode = '';
            this.projectCodeError = null;
            this.rows = [];
            this.uploadFile = null;
            this.dryRunResult = null;
            this.busy = null;
            this.showOnlyIssues = false;
            // 탭1 상태
            this.fileUploadModel = null;
            this.fileBulkBusy = false;
            this.fileBulkResult = null;
            // 탭 자체
            this.activeTab = 0;
        },
    },
};
</script>

<style scoped>
.toolbar .file-input {
    max-width: 280px;
}
.bulk-table-wrapper {
    max-height: 360px;
    overflow: auto;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}
.bulk-table {
    margin: 0;
}
.bulk-table thead th {
    position: sticky;
    top: 0;
    background: #f8f9fa;
    z-index: 1;
}
.bulk-table .idx-col { width: 44px; }
.bulk-table .company-id-col { width: 120px; }
.bulk-table .vendor-code-col { width: 200px; }
.bulk-table .status-col { width: 200px; }
.bulk-table .action-col { width: 44px; }
.bulk-table td { vertical-align: middle; }
.status-detail {
    font-size: 0.78rem;
    line-height: 1.2;
    margin-top: 2px;
    word-break: break-word;
}
.row-ok td { background-color: rgba(40, 167, 69, 0.05); }
.row-error td { background-color: rgba(220, 53, 69, 0.07); }
.remove-btn { line-height: 1; }
.copy-id-btn {
    font-size: 0.72rem;
    padding: 1px 6px;
    line-height: 1.3;
}
.regen-btn {
    font-size: 0.72rem;
    padding: 1px 6px;
    line-height: 1.3;
}
.candidate-name {
    font-size: 0.75rem;
    margin-bottom: 2px;
}
.issue-actions {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    font-size: 0.85rem;
}
.bulk-apply-btn {
    font-size: 0.78rem;
    padding: 2px 10px;
}
.issue-filter {
    font-size: 0.82rem;
}
.footer-actions { border-top: 1px solid #dee2e6; padding-top: 12px; }
kbd {
    background: #f1f1f1;
    border: 1px solid #ccc;
    border-radius: 3px;
    padding: 0 4px;
    font-size: 0.75rem;
}
</style>
